'use client'

import { useCallback, useMemo, useRef } from 'react'

const LONG_PRESS_MS = 450
/** Finger travel that cancels a long press (it's a drag or scroll instead). */
const LONG_PRESS_SLOP = 8

/**
 * Long press for touch, plus the equivalents on other inputs: right click /
 * Android's contextmenu event, the ContextMenu key and Shift+F10. The click
 * that ends a long press is swallowed so it doesn't also "tap" the element.
 *
 * iOS Safari never fires contextmenu on long press, hence the timer.
 */
export function useLongPress(onLongPress: (() => void) | undefined) {
  const state = useRef<{ timer: ReturnType<typeof setTimeout> | null; x: number; y: number; fired: boolean }>({
    timer: null,
    x: 0,
    y: 0,
    fired: false,
  })

  const cancel = useCallback(() => {
    if (state.current.timer) clearTimeout(state.current.timer)
    state.current.timer = null
  }, [])

  const handlers = useMemo(
    () => ({
      onPointerDown(event: React.PointerEvent) {
        state.current.fired = false
        if (!onLongPress || event.pointerType === 'mouse') return
        state.current.x = event.clientX
        state.current.y = event.clientY
        state.current.timer = setTimeout(() => {
          state.current.timer = null
          state.current.fired = true
          // Android only; iOS Safari has no vibration API.
          if ('vibrate' in navigator) navigator.vibrate(10)
          onLongPress()
        }, LONG_PRESS_MS)
      },
      onPointerMove(event: React.PointerEvent) {
        if (!state.current.timer) return
        if (
          Math.abs(event.clientX - state.current.x) > LONG_PRESS_SLOP ||
          Math.abs(event.clientY - state.current.y) > LONG_PRESS_SLOP
        ) {
          cancel()
        }
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onContextMenu(event: React.MouseEvent) {
        if (!onLongPress) return
        event.preventDefault()
        // Android fires contextmenu on long press too; the timer already ran.
        if (!state.current.fired) onLongPress()
      },
      onKeyDown(event: React.KeyboardEvent) {
        if (onLongPress && (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))) {
          event.preventDefault()
          onLongPress()
        }
      },
      onClickCapture(event: React.MouseEvent) {
        if (!state.current.fired) return
        state.current.fired = false
        event.preventDefault()
        event.stopPropagation()
      },
    }),
    [onLongPress, cancel]
  )

  return { handlers, cancel, didFire: () => state.current.fired }
}
